// shabba-dashboard/src/pages/AnalysisPage.tsx
// Consolidated trading intelligence hub - merging advanced analytics + profitability prediction
// This replaces both the old AnalysisPage and ProfitabilityPage for unified trading decisions

import React from 'react';
import { BarChart3, TrendingUp, Target, Zap, Activity, AlertTriangle, CheckCircle, DollarSign } from 'lucide-react';
import { useMarket } from '../context/MarketContext';
import { AnalyticsCard, AnalyticsGrid, AnalyticsSection } from '../components/analytics/AnalyticsCard';
import { DepthImbalanceChart } from '../components/analytics/DepthImbalanceChart';
import { LiquidityHeatmap } from '../components/analytics/LiquidityHeatmap';
import { StalenessIndicator } from '../components/analytics/StalenessIndicator';
import { RiskMetricsCard } from '../components/analytics/EnhancedAnalytics';
import { PredictiveAnalytics } from '../components/analytics/PredictiveAnalytics';
import EnhancedPriceChart from '../components/analytics/EnhancedPriceChart';
import SimpleVWAPChart from '../components/SimpleVWAPChart';
import SimpleTWAPChart from '../components/SimpleTWAPChart';
import ProfitabilityPredictor from '../components/analytics/ProfitabilityPredictor';
import OrderBookAnalyzer from '../components/analytics/OrderBookAnalyzer';
import AttributionDashboard from '../components/analytics/AttributionDashboard';
import VWAPMicropriceAnalyzer from '../components/analytics/VWAPMicropriceAnalyzer';
import BookChanges from '../components/analytics/BookChanges';
import { toEAT } from '../utils/time';

export default function AnalysisPage() {
  const {
    orderBook,
    priceHistory,
    selectedSymbol,
    labelForSecId,
    analytics,
    recentTrades,
    stats,
    currentPrice,
    connectionStatus,
    lastUpdateTime,
    micropriceDataBySymbol
  } = useMarket();

  // Check for sufficient data
  const hasMinimumData = recentTrades && recentTrades.length >= 5 && currentPrice && orderBook?.bids?.length;

  if (!analytics) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        color: '#94a3b8'
      }}>
        <BarChart3 size={48} />
        <h3>Loading Advanced Analytics...</h3>
        <p>Calculating market microstructure and profitability signals...</p>
      </div>
    );
  }

  const {
    depthImbalance,
    microprice,
    staleness,
    orderFlowImbalance,
    fillProbability,
    volatility,
    liquidityHeatmap,
    riskMetrics
  } = analytics;

  // Microprice should come from VPS (via analytics.microprice which uses VPS data)
  // No fallback calculation - if VPS data not available, microprice will be from order book only

  const lastTrade = recentTrades.find(t => t.symbol ? t.symbol.toUpperCase() === selectedSymbol.toUpperCase() : true);
  const lastUpdateDisplay = (() => {
    if (!lastUpdateTime) return 'N/A';
    const ts = Date.parse(lastUpdateTime);
    if (!Number.isFinite(ts)) return lastUpdateTime;
    const ageSec = Math.max(0, Math.floor((Date.now() - ts) / 1000));
    const hhmm = new Date(ts).toLocaleTimeString();
    const ageStr = ageSec < 60 ? `${ageSec}s ago` : ageSec < 3600 ? `${(ageSec/60).toFixed(1)}m ago` : `${(ageSec/3600).toFixed(1)}h ago`;
    return `${hhmm} (${ageStr})`;
  })();

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 20,
      minHeight: '100vh',
      backgroundColor: '#0f172a',
      padding: 16
    }}>
      {/* Header */}
      <div style={{
        background: '#1e293b',
        padding: 16,
        borderRadius: 8,
        border: '1px solid #334155',
        marginBottom: 8
      }}>
        <div className="intelligence-hub-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div className="intelligence-hub-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <BarChart3 size={16} color="#94a3b8"/>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
              Trading Intelligence Hub — {labelForSecId(selectedSymbol)}
            </h3>
          </div>
          <div className="intelligence-hub-badges" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              backgroundColor: hasMinimumData ? '#10b98115' : '#f59e0b15',
              border: `1px solid ${hasMinimumData ? '#10b98130' : '#f59e0b30'}`,
              borderRadius: 4,
              padding: '4px 8px',
              fontSize: 11,
              fontWeight: 600,
              color: hasMinimumData ? '#10b981' : '#f59e0b'
            }}>
              {hasMinimumData ? <CheckCircle size={12} /> : <AlertTriangle size={12} />}
              {hasMinimumData ? 'OPTIMAL DATA' : 'LIMITED DATA'}
            </div>
            <div style={{
              backgroundColor: '#3b82f615',
              color: '#3b82f6',
              fontSize: 11,
              fontWeight: 600,
              padding: '4px 8px',
              borderRadius: 4,
              border: '1px solid #3b82f630'
            }}>
              ENHANCED ANALYTICS
            </div>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            marginLeft: 8,
            fontSize: 11,
            color: '#94a3b8'
          }}>
            <div><strong style={{ color:'#e2e8f0' }}>Last Update:</strong> {toEAT(lastUpdateDisplay)}</div>
            {lastTrade && (
              <div>
                <strong style={{ color:'#e2e8f0' }}>Last Trade:</strong> {lastTrade.side.toUpperCase()} {lastTrade.size.toLocaleString()} @ {lastTrade.price.toFixed(2)}
              </div>
            )}
          </div>
          </div>
        </div>
        <div style={{ fontSize: 12, color: '#94a3b8' }}>
          Complete market analysis with real-time profitability predictions and advanced algorithms
        </div>
      </div>

      {/* ═══ SECTION 1: PROFITABILITY PREDICTION (PRIMARY FOCUS) ═══ */}
      <AnalyticsSection title="🎯 Real-Time Order Book Analyzer">
        <div className="analysis-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {/* <ProfitabilityPredictor
            spreadBps={150}
            maxPosition={300}
            capitalTZS={50000000}
            showDetails={true}
          /> */}


          {/* Book Changes */}
          <BookChanges secId={selectedSymbol}/>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <OrderBookAnalyzer />
            <div style={{
              backgroundColor: '#1e293b',
              borderRadius: 8,
              padding: 12,
              border: '1px solid #334155',
              flex: 1
            }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: 12, color: '#94a3b8', fontWeight: 600 }}>
                QUICK STATS
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 11 }}>
                <div>
                  <div style={{ color: '#64748b' }}>Fill Probability</div>
                  <div style={{ color: '#f1f5f9', fontWeight: 600 }}>
                    {(fillProbability.probability * 100).toFixed(0)}%
                  </div>
                </div>
                <div>
                  <div style={{ color: '#64748b' }}>Queue Position</div>
                  <div style={{ color: '#f1f5f9', fontWeight: 600 }}>
                    #{fillProbability.queuePosition}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#64748b' }}>Spread Quality</div>
                  <div style={{
                    color: microprice.spread < 0.01 ? '#10b981' :
                          microprice.spread > 0.05 ? '#ef4444' : '#f59e0b',
                    fontWeight: 600
                  }}>
                    {microprice.spread < 0.01 ? 'TIGHT' :
                     microprice.spread > 0.05 ? 'WIDE' : 'NORMAL'}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#64748b' }}>Data Quality</div>
                  <div style={{
                    color: staleness.isStale ? '#ef4444' : '#10b981',
                    fontWeight: 600
                  }}>
                    {staleness.isStale ? 'STALE' : 'FRESH'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </AnalyticsSection>

      {/* ═══ SECTION 2: ENHANCED PRICE ANALYSIS ═══ */}
      <AnalyticsSection title="📊 Enhanced Price & Volume Analysis">
        {/* Simple Database Charts */}
        {/* <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <SimpleVWAPChart symbol={selectedSymbol} height={300} />
          <SimpleTWAPChart symbol={selectedSymbol} height={300} />
        </div> */}
        
        <div style={{ marginBottom: 16 }}>
          <EnhancedPriceChart
            height={400}
            showVolume={false}
            timeRange={4}
          />
        </div>
        <VWAPMicropriceAnalyzer />
      </AnalyticsSection>

      {/* ═══ SECTION 3: PREDICTIVE ANALYTICS ═══ */}
      <AnalyticsSection title="🔮 Predictive Analytics & Market Intelligence">
        <PredictiveAnalytics
          baseAnalytics={analytics}
          orderBook={orderBook}
          recentTrades={recentTrades}
          currentPrice={currentPrice}
        />
      </AnalyticsSection>

      {/* ═══ SECTION 4: MARKET DYNAMICS & RISK ═══ */}
      <AnalyticsSection title="📈 Market Dynamics & Risk Assessment">
        <div className="analysis-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
          <AnalyticsCard
            title="Microprice"
            value={microprice.microprice.toFixed(2)}
            subtitle={`Spread: ${microprice.spread.toFixed(2)}`}
            icon={<Target size={16} color="#3b82f6" />}
            color="#3b82f6"
          />

          <AnalyticsCard
            title="Recommended Spread"
            value={volatility.recommendedSpread.toFixed(2)}
            subtitle={`${(volatility.spreadMultiplier * 100).toFixed(0)}% of current`}
            icon={<Zap size={16} color="#10b981" />}
            color="#10b981"
          />

          <AnalyticsCard
            title="Fill Probability"
            value={`${(fillProbability.probability * 100).toFixed(0)}%`}
            subtitle={`Queue: ${fillProbability.queuePosition} | Wait: ${fillProbability.estimatedWaitTime}s`}
            icon={<Activity size={16} color="#8b5cf6" />}
            status={fillProbability.confidence === 'high' ? 'good' :
                    fillProbability.confidence === 'medium' ? 'warning' : 'danger'}
          />
        </div>

        <div className="analysis-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <RiskMetricsCard data={riskMetrics} />
          <div style={{
            backgroundColor: '#1e293b',
            padding: 16,
            borderRadius: 8,
            border: '1px solid #334155'
          }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: 14, fontWeight: 600, color: '#e2e8f0' }}>
              Trading Signals Summary
            </h4>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12 }}>
              <div style={{
                padding: 8,
                backgroundColor: depthImbalance.imbalance > 0.1 ? '#10b98115' :
                                 depthImbalance.imbalance < -0.1 ? '#ef444415' : '#0f172a',
                borderRadius: 4,
                border: `1px solid ${depthImbalance.imbalance > 0.1 ? '#10b98130' :
                                      depthImbalance.imbalance < -0.1 ? '#ef444430' : '#334155'}`
              }}>
                <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>Market Bias</div>
                <div style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: depthImbalance.imbalance > 0.1 ? '#10b981' :
                         depthImbalance.imbalance < -0.1 ? '#ef4444' : '#94a3b8'
                }}>
                  {depthImbalance.imbalance > 0.1 ? 'BULLISH' :
                   depthImbalance.imbalance < -0.1 ? 'BEARISH' : 'NEUTRAL'}
                </div>
              </div>

              <div style={{
                padding: 8,
                backgroundColor: microprice.microprice > currentPrice ? '#ef444415' :
                                 microprice.microprice < currentPrice ? '#10b98115' : '#0f172a',
                borderRadius: 4,
                border: `1px solid ${microprice.microprice > currentPrice ? '#ef444430' :
                                      microprice.microprice < currentPrice ? '#10b98130' : '#334155'}`
              }}>
                <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>Price Signal</div>
                <div style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: microprice.microprice > currentPrice ? '#ef4444' :
                         microprice.microprice < currentPrice ? '#10b981' : '#94a3b8'
                }}>
                  {microprice.microprice > currentPrice ? 'OVERPRICED' :
                   microprice.microprice < currentPrice ? 'UNDERPRICED' : 'FAIR'}
                </div>
              </div>

              <div style={{
                padding: 8,
                backgroundColor: orderFlowImbalance.flowRatio > 0.1 ? '#10b98115' :
                                 orderFlowImbalance.flowRatio < -0.1 ? '#ef444415' : '#0f172a',
                borderRadius: 4,
                border: `1px solid ${orderFlowImbalance.flowRatio > 0.1 ? '#10b98130' :
                                      orderFlowImbalance.flowRatio < -0.1 ? '#ef444430' : '#334155'}`
              }}>
                <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>Order Flow</div>
                <div style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: orderFlowImbalance.flowRatio > 0.1 ? '#10b981' :
                         orderFlowImbalance.flowRatio < -0.1 ? '#ef4444' : '#94a3b8'
                }}>
                  {orderFlowImbalance.flowRatio > 0.1 ? 'BUY FLOW' :
                   orderFlowImbalance.flowRatio < -0.1 ? 'SELL FLOW' : 'NEUTRAL'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </AnalyticsSection>

      {/* ═══ SECTION 5: ORDER FLOW & LIQUIDITY ANALYSIS ═══ */}
      <AnalyticsSection title="💹 Order Flow & Liquidity Intelligence">
        <div className="analysis-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <DepthImbalanceChart
            data={depthImbalance}
            overall={analytics.fullBookImbalance}
          />


          <div style={{
            background: '#1e293b',
            padding: 16,
            borderRadius: 8,
            border: '1px solid #334155'
          }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: 14, fontWeight: 600, color: '#e2e8f0' }}>
              Order Flow Imbalance ({orderFlowImbalance.timeWindow}s)
            </h4>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>Buy Flow</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#10b981' }}>
                  {orderFlowImbalance.buyFlow.toLocaleString()}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>Sell Flow</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#ef4444' }}>
                  {orderFlowImbalance.sellFlow.toLocaleString()}
                </div>
              </div>
            </div>

            <div style={{
              padding: 8,
              background: orderFlowImbalance.flowRatio > 0.1 ? '#10b98120' :
                         orderFlowImbalance.flowRatio < -0.1 ? '#ef444420' : '#334155',
              borderRadius: 4,
              border: `1px solid ${orderFlowImbalance.flowRatio > 0.1 ? '#10b98140' :
                                   orderFlowImbalance.flowRatio < -0.1 ? '#ef444440' : '#334155'}`
            }}>
              <div style={{
                fontSize: 12,
                fontWeight: 600,
                color: orderFlowImbalance.flowRatio > 0.1 ? '#10b981' :
                       orderFlowImbalance.flowRatio < -0.1 ? '#ef4444' : '#94a3b8',
                textAlign: 'center'
              }}>
                Net Flow: {orderFlowImbalance.flowRatio > 0 ? '+' : ''}{(orderFlowImbalance.flowRatio * 100).toFixed(1)}%
              </div>
            </div>
          </div>
        </div>

        <div className="analysis-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <StalenessIndicator data={staleness} baselineTimestamp={analytics.timestamp} lastUpdateTimestamp={lastUpdateTime} />
          <LiquidityHeatmap data={liquidityHeatmap} />
        </div>
      </AnalyticsSection>

      {/* ═══ SECTION 6: PROFIT ATTRIBUTION & PERFORMANCE ═══ */}
      <AnalyticsSection title="💰 Profit Attribution & Strategy Performance">
        <AttributionDashboard />
      </AnalyticsSection>
    </div>
  );
}